import { useEffect, useMemo, useCallback } from "react";
import { useScannerContext } from "./ScannerContext";
import { Scanner } from "./types";
import { appLog } from "./helpers";
import { defaultScanner } from "./constants";
import { canUseWebSerialScanner, createWebSerialScanner, WEB_SERIAL_SCANNER_FRIENDLY_NAME } from "./WebSerialScanner";

const hasEnterpriseBrowser = () => typeof window !== "undefined" && typeof window.EB !== "undefined";
const preferWebSerial = () => canUseWebSerialScanner();

const ScannerInitializer = () => {
  const { setScanners, selectScanner } = useScannerContext();

  const preferWebSerialMemo = useMemo(() => preferWebSerial(), []);
  const enumerateScanners = useCallback((): Scanner[] => {
    appLog("enumerateScanners", true);

    // Try Enterprise Browser first if available
    if (hasEnterpriseBrowser()) {
      try {
        const enumeratedScanners = window.EB?.Barcode.enumerate?.();
        if (Array.isArray(enumeratedScanners)) {
          const filtered = enumeratedScanners.filter((scanner) => scanner?.friendlyName);
          if (filtered.length > 0) {
            appLog("[enumerateScanners] using Enterprise Browser scanners", true);
            return filtered;
          }
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err ?? "unknown error");
        appLog(
          `[enumerateScanners][enterprise] ${message || "unknown"}`,
          true,
          "error",
          err instanceof Error ? err : { error: err }
        );
      }
    }

    // Fallback to Web Serial if EB unavailable or has no scanners
    if (preferWebSerialMemo) {
      appLog("[enumerateScanners] using Web Serial scanner", true);
      return [createWebSerialScanner()];
    }

    // Final fallback to stub scanners
    return [
      { friendlyName: "Bluetooth Scanner" },
      { friendlyName: "2D Barcode Imager" },
      { friendlyName: "Stub Scanner" },
    ].map((curr) => ({ ...curr, enable: () => {} }));
  }, [preferWebSerialMemo]);

  useEffect(() => {
    const initialize = async () => {
      appLog(typeof window.EB === "undefined" ? "EB unavailable." : "EB available.", true);
      appLog(window.EB?.Notification ? "EB beep available." : "EB beep unavailable.", true);

      const scannerArray = enumerateScanners();
      setScanners(scannerArray);

      if (!scannerArray || scannerArray.length === 0) {
        return;
      }

      setTimeout(() => {
        appLog("[ScannerInitializer] select default scanner", true);
        
        // Determine expected scanner based on what's actually available
        let expectedFriendlyName: string;
        
        if (hasEnterpriseBrowser() && scannerArray.some(s => s.friendlyName === defaultScanner)) {
          // Enterprise Browser is available and has the default scanner
          expectedFriendlyName = defaultScanner;
        } else if (preferWebSerialMemo && scannerArray.some(s => s.friendlyName === WEB_SERIAL_SCANNER_FRIENDLY_NAME)) {
          // Fallback to Web Serial if EB unavailable
          expectedFriendlyName = WEB_SERIAL_SCANNER_FRIENDLY_NAME;
        } else {
          // Use the first available scanner
          expectedFriendlyName = scannerArray[0]?.friendlyName || defaultScanner;
        }

        const defaultScannerObj = scannerArray.find((s) => s.friendlyName === expectedFriendlyName);

        if (!defaultScannerObj) {
          appLog(
            `[ScannerInitializer] preferred scanner ${expectedFriendlyName} not found, selecting first entry`,
            true,
            "warn"
          );
          selectScanner(scannerArray[0]);
          return;
        }

        appLog(`[ScannerInitializer] selected scanner: ${expectedFriendlyName}`, true);
        selectScanner(defaultScannerObj);
      }, 1000);

      appLog("Barcode Scanner Application initialized");
    };

    initialize();
  }, [enumerateScanners, preferWebSerialMemo, selectScanner, setScanners]);

  return null;
};

export default ScannerInitializer;
